import { X } from 'lucide-react';
import { ModalBackdrop } from '../common';
import BranchLocationField from './BranchLocationField';
import EntityAvatar from './EntityAvatar';
import EntityStatusPicker from './EntityStatusPicker';

function EntityEditModal({
  values,
  errors,
  statusOptions,
  isSaving,
  onChange,
  onClearPhoto,
  onClose,
  onSubmit,
  entityLabel = 'Supplier',
}) {
  const entityLabelLower = entityLabel.toLowerCase();
  const showsSacksMark = entityLabel === 'Supplier';
  const showsBranchLocations = entityLabel === 'Customer';

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.28)]">
      <div className="mx-auto w-full max-w-3xl rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] shadow-[0_24px_60px_rgba(var(--theme-shadow-rgb),0.16)] sm:rounded-[28px]">
        <div className="flex items-start justify-between gap-4 border-b border-[#efe5dd] px-4 py-4 sm:px-7 sm:py-5">
          <div>
            <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
              {entityLabel} Module
            </p>
            <h2 className="m-0 mt-2 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
              Edit {entityLabel}
            </h2>
            <p className="m-0 mt-2 text-[0.95rem] text-[var(--theme-text-muted)]">
              Update {entityLabelLower} details and account status in one place.
            </p>
          </div>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#e6dbd0] bg-white text-[#6e6257] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label={`Close edit ${entityLabelLower} modal`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form className="px-4 py-4 sm:px-7 sm:py-6" onSubmit={onSubmit}>
          <section className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.025)] sm:p-5">
            <div className="mb-4">
              <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
                {entityLabel} Details
              </h3>
              <p className="m-0 mt-2 text-[0.9rem] text-[var(--theme-text-muted)]">
                Keep the record current for daily {entityLabelLower} management.
              </p>
            </div>

            <div className="mb-5 grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)]">
              <div className="flex flex-col items-center rounded-[20px] border border-[#ebe2d9] bg-[var(--theme-surface-muted)] p-4 text-center">
                <EntityAvatar
                  photoUrl={values.photoUrl}
                  name={values.companyName}
                  entityLabel={entityLabel}
                  className="h-28 w-28"
                  textClassName="text-[2rem] font-semibold"
                />
                <span className="mt-4 text-[0.88rem] font-medium text-[#5b5048]">
                  {entityLabel} Photo
                </span>
                <span className="mt-1 text-[0.82rem] leading-6 text-[var(--theme-text-muted)]">
                  Keep the {entityLabelLower} photo current so records stay easy to recognize.
                </span>
                <label className="mt-4 inline-flex cursor-pointer items-center rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.84rem] font-medium text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)]">
                  Update Photo
                  <input
                    className="sr-only"
                    type="file"
                    name="photoUrl"
                    accept="image/*"
                    onChange={onChange}
                  />
                </label>
                {values.photoUrl ? (
                  <button
                    className="mt-3 inline-flex items-center rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-warm)] px-4 py-2 text-[0.84rem] font-medium text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    onClick={onClearPhoto}
                    disabled={isSaving}
                  >
                    Remove Photo
                  </button>
                ) : null}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormInput
                  label="Company Name"
                  name="companyName"
                  value={values.companyName}
                  error={errors.companyName}
                  onChange={onChange}
                  placeholder="Enter company name"
                />
                <FormInput
                  label="Contact Number (optional)"
                  name="contactNumber"
                  value={values.contactNumber}
                  error={errors.contactNumber}
                  onChange={onChange}
                  placeholder="09XXXXXXXXX"
                />
                {showsSacksMark ? (
                  <FormInput
                    label="Sacks Mark"
                    name="sacksMark"
                    value={values.sacksMark}
                    error={errors.sacksMark}
                    onChange={onChange}
                    placeholder="Auto-generated from company name"
                  />
                ) : null}
                {showsBranchLocations ? (
                  <BranchLocationField
                    value={values.branchLocations}
                    error={errors.branchLocations}
                    onChange={onChange}
                    disabled={isSaving}
                    className="md:col-span-2"
                  />
                ) : (
                  <FormInput
                    label="Address"
                    name="address"
                    value={values.address}
                    error={errors.address}
                    onChange={onChange}
                    placeholder={`Enter ${entityLabelLower} address`}
                  />
                )}
                <div className="md:col-span-2 rounded-[20px] border border-[#ebe2d9] bg-[var(--theme-surface-muted)] p-4">
                  <EntityStatusPicker
                    value={values.status}
                    options={statusOptions}
                    onChange={onChange}
                    error={errors.status}
                  />
                  <p className="m-0 mt-3 text-[0.88rem] leading-6 text-[var(--theme-text-subtle)]">
                    Selected status: <span className="font-medium text-[var(--theme-text-strong)]">{values.status}</span>.
                    Save changes to apply this update to the {entityLabelLower} record.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="mt-6 grid gap-3 sm:mt-8 sm:flex sm:flex-wrap sm:justify-end">
            <button
              className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-7 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#d8cec4] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
}

function FormInput({ label, name, value, error, onChange, placeholder, className }) {
  return (
    <label className={`grid self-start gap-2 ${className || ''}`}>
      <span className="text-[0.88rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`h-[48px] rounded-[16px] border bg-[var(--theme-surface-warm)] px-4 text-[0.95rem] outline-none transition placeholder:text-[#b3a79c] ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
        }`}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <span
        className={`min-h-[16px] text-[0.8rem] leading-4 ${
          error ? 'text-[#bb5745]' : 'invisible text-transparent'
        }`}
      >
        {error || '-'}
      </span>
    </label>
  );
}

export default EntityEditModal;
